import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import * as authApi from './auth.api';
import { IDLE_LOGGED_OUT_KEY } from './idle/useIdleLogout';

import type { LoginRequest } from '@utazzo/shared';

import { isMarketingDashboardRole, MARKETING_DASHBOARD_PATH, WELCOME_PATH } from '@/app/home-path';
import { useWelcomeGate } from '@/app/welcome-gate';
import { releasePushForLogout } from '@/features/push/web-push-client';
import { extractServerError } from '@/lib/api-error';
import { useAuthStore } from '@/stores/auth-store';

/**
 * React hooks wrapping the auth API surface. UI components should consume
 * these hooks rather than calling the API functions directly - they handle
 * store updates, toasts and redirects in one place.
 */

export const useCurrentUser = () => useAuthStore((s) => s.user);
export const useIsAuthenticated = () => useAuthStore((s) => s.isAuthenticated);

/**
 * 2026-07-23. Bulk data export (Excel / CSV) is ADMIN-only.
 *
 * An export pulls EVERY row matching the current filters into a file that
 * leaves the product - customer contacts, budgets, agent performance. That is
 * a different risk from reading the same rows on screen one page at a time,
 * so it is gated on the role rather than on whatever read permission the grid
 * itself needs.
 *
 * ONE definition used by every export surface (the DataGrid toolbar button
 * that all list screens share, plus the Daily Tracker report's own buttons),
 * so a role can never end up with an export button on one screen and not
 * another. Purely a UI gate - the underlying list endpoints are unchanged and
 * still RBAC-scoped per role.
 */
export const useCanExportData = (): boolean => useAuthStore((s) => s.user?.role === 'ADMIN');

/**
 * Export gate for finance / accounting listings (Suppliers, Customers, Airlines,
 * Branches, Query Sources, Sub Branches, Invoices, Credit Notes). ADMIN plus the
 * FINANCE role - finance owns these records, so they can pull the full sheet.
 * Scoped to those screens only; marketing/sales exports stay ADMIN-only via
 * `useCanExportData`.
 */
export const useCanExportFinanceData = (): boolean =>
  useAuthStore((s) => s.user?.role === 'ADMIN' || s.user?.role === 'FINANCE');

export const useLogin = () => {
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: LoginRequest) => authApi.login(payload),
    onSuccess: (data) => {
      // A fresh sign-in clears any lingering "signed out due to inactivity"
      // notice so it can't reappear after a later manual logout.
      sessionStorage.removeItem(IDLE_LOGGED_OUT_KEY);
      setSession(data.user, data.tokens);
      // Marketing-only role (marketing_in): skip Welcome entirely and land on
      // the Marketing dashboard - their only screen. Keyed on the role code
      // (role-specific, works for any user holding it), not on a single user.
      if (isMarketingDashboardRole(data.user.assignedRoleCodes)) {
        navigate(MARKETING_DASHBOARD_PATH, { replace: true });
        return;
      }
      // EVERY other login lands on the Welcome Utazzer page first and re-arms
      // the welcome gate - so even a re-login after being bounced off a deep
      // page (e.g. Leads) shows welcome again, not that page. Non-admins must
      // then scroll the whole page before they can navigate anywhere (see
      // welcome-gate + AppShell). The old "return to the page you were on"
      // behaviour is deliberately dropped.
      useWelcomeGate.getState().requireWelcome();
      navigate(WELCOME_PATH, { replace: true });
    },
    onError: (err: unknown) => {
      // 401 covers both "unknown email" and "wrong password" - the server
      // returns the same "Invalid credentials" for both on purpose (anti
      // enumeration). Show the friendlier localized line for that case;
      // surface the real server message for everything else (deactivated
      // account, password-reset-required, network/5xx).
      const message =
        statusCodeOf(err) === 401
          ? t('auth.login.invalidCredentials')
          : (extractErrorMessage(err) ?? t('auth.login.genericError'));
      toast.error(message);
    },
  });
};

export const useLogout = () => {
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      // Stop phone / desktop push to this device first - the call needs the
      // session that logout is about to end. Never blocks the logout.
      await releasePushForLogout();
      return authApi.logout('manual');
    },
    onSettled: () => {
      clear();
      navigate('/login', { replace: true });
    },
  });
};

export const useForgotPassword = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
    onSuccess: () => toast.success(t('auth.forgot.success')),
    onError: (err: unknown) => {
      const message = extractErrorMessage(err) ?? t('auth.login.genericError');
      toast.error(message);
    },
  });
};

export const useResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: authApi.resetPassword,
    onSuccess: () => {
      toast.success(t('auth.reset.success'));
      navigate('/login', { replace: true });
    },
    onError: (err: unknown) => {
      const message = extractErrorMessage(err) ?? t('auth.login.genericError');
      toast.error(message);
    },
  });
};

/**
 * The axios interceptor rejects with the *flat* server envelope
 * `{ statusCode, message, code, details, ... }` (see `api-client.ts`), NOT a
 * nested `{ error: { message } }`. Reuse the shared extractor so the auth
 * screens surface the real server message ("Invalid credentials", "Account
 * deactivated", "A password reset is required...") instead of the generic
 * fallback, and so Prisma/SQL dumps stay sanitised.
 */
const extractErrorMessage = (err: unknown): string | null => {
  const { message } = extractServerError(err);
  if (message) return message;
  if (err instanceof Error) return err.message;
  return null;
};

/** Pull the HTTP status off the flat server envelope, when present. */
const statusCodeOf = (err: unknown): number | null => {
  if (err && typeof err === 'object' && 'statusCode' in err) {
    const code = (err as { statusCode?: unknown }).statusCode;
    if (typeof code === 'number') return code;
  }
  return null;
};
